import React, { useState } from "react";
import authLogo from "../../../assets/auth_logo.png";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { usePostRegisterMutation } from "../../../rtk-query/slices/auth/authSlice";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useGetUserProfileQuery } from "../../../rtk-query/slices/profile/profileSlice";

function SignupCard() {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [postRegister, { isLoading }] = usePostRegisterMutation();
  const {
    refetch,
    fetching: isUserDataFetching,
    loading: isUserDataLoading,
  } = useGetUserProfileQuery();

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Please enter your email address"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Please enter a password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please enter a confirm password"),
    }),
    onSubmit: async (values, { resetForm, setFieldError }) => {
      // Handle form submission here

      try {
        localStorage.removeItem("DODAdminCreatedBy");
        const data = {
          email: values.email,
          password: values.password,
          confirm_password: values.confirmPassword,
        };
        const response = await postRegister(data);

        if (response?.data?.status) {
          toast.success(response?.data?.message, {
            position: "top-center",
            autoClose: 3000,
          });
          localStorage.setItem("DODLoginToken", response?.data?.token);
          refetch();
          resetForm(); // Clear the form
          localStorage.setItem("DODUserEmailToVerify", values.email);

          localStorage.setItem("DODUserEmailToVerifyNext", "onboarding"); // Clear the stored user email

          navigate(`/verify-token`); // Redirect to the onboarding screen
        }
        if (response?.error) {
          const errors = response?.error?.data?.errors;
          if (errors) {
            for (const field in errors) {
              if (errors.hasOwnProperty(field)) {
                const combinedErrors = errors[field].join(" ");
                if (field === "confirm_password") {
                  setFieldError("confirmPassword", combinedErrors);
                }
                if (field === "password") {
                  setFieldError("password", combinedErrors);
                }
                if (field === "email") {
                  setFieldError("email", combinedErrors);
                }
              }
            }
            toast.error(response?.error?.data.message, {
              position: "top-center",
              autoClose: 3000,
            });
          } else {
            toast.error(response?.error?.data.message, {
              position: "top-center",
              autoClose: 3000,
            });
          }
        }
      } catch (error) {
        console.log("Errors:", error);
        toast.error("An unexpected error occurred. Please try again.");
      }
    },
  });
  return (
    <div className="auth-card">
      <div className="auth-card-header d-flex align-items-center justify-content-center">
        <img src={authLogo} alt="logo" width={181} height={61} />
      </div>
      <div className="auth-card-body">
        {/* <h1 className="auth-card-heading mb-2">Create a new account</h1>
        // <p className="auth-card-description">
        //   Signup with your company or individual details to get supplier access
        // </p>*/}
        <form onSubmit={formik.handleSubmit} className="auth-card-form mt-3">
          {/* email input field */}
          <div className="auth-card-form-group d-flex flex-column align-items-start gap-1">
            <label className="input-label" htmlFor="email">
              Email Address
            </label>
            <input
              className={`input-field ${
                formik.touched.email && formik.errors.email ? "is-invalid" : ""
              }`}
              type="email"
              disabled={isLoading}
              id="email"
              // placeholder="Email Address"
              {...formik.getFieldProps("email")}
            />
            {formik.touched.email && formik.errors.email ? (
              <div className="invalid-feedback">{formik.errors.email}</div>
            ) : null}
          </div>

          {/* password input field */}
          <div className="position-relative auth-card-form-group d-flex flex-column align-items-start gap-1 mt-3">
            <label className="input-label" htmlFor="password">
              Password
            </label>
            <input
              className={`input-field ${
                formik.touched.password && formik.errors.password
                  ? "is-invalid"
                  : ""
              }`}
              type={showPassword ? "text" : "password"}
              id="password"
              // placeholder="Password"
              {...formik.getFieldProps("password")}
            />
            {formik.touched.password && formik.errors.password ? (
              <div className="invalid-feedback">{formik.errors.password}</div>
            ) : null}
          </div>

          {/* confirm password input field */}
          <div className="position-relative auth-card-form-group d-flex flex-column align-items-start gap-1 mt-3">
            <label className="input-label" htmlFor="confirmPassword">
              Confirm Password
            </label>
            <input
              className={`input-field ${
                formik.touched.confirmPassword && formik.errors.confirmPassword
                  ? "is-invalid"
                  : ""
              }`}
              disabled={isLoading}
              type={showPassword ? "text" : "password"}
              id="confirmPassword"
              // placeholder="Confirm Password"
              {...formik.getFieldProps("confirmPassword")}
            />
            <p
              onClick={() => setShowPassword(!showPassword)}
              className="position-absolute input-field-toggle"
            >
              {showPassword ? "Hide" : "show"}
            </p>
            {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
              <div className="invalid-feedback">
                {formik.errors.confirmPassword}
              </div>
            ) : null}
          </div>

          {/* create account button */}
          <button
            disabled={isLoading}
            type="submit"
            className="w-100 auth-login-btn mt-3"
          >
            Create your account {isLoading && <Spinner size="sm" />}
          </button>
          {/* terms and conditions */}
          <p className="auth-card-terms mt-4">
            By proceeding, you agree to the <span>Privacy Policy</span> and{" "}
            <span>Terms & Conditions</span>
          </p>
        </form>
      </div>
      <div className="auth-card-footer pt-3 pb-3">
        <p>
          Already have an account? <Link to="/login">Sign In</Link>
        </p>
      </div>
    </div>
  );
}

export default SignupCard;
